/**
 * Drawing an envelope (designs.ts): the two printed sides of its sheet and the
 * finished envelope, front and back.
 *
 * Side 1 is the inside and carries the guides - solid lines to cut, dashed
 * lines to fold (numbered by step), grey patches to glue, where the card goes,
 * the round seal sticker. Side 2 is the outside, printed on the back of the
 * sheet (two-sided, flipped on the long edge, like the card): the card's own
 * paper all over it, the name on the front in a label, and a seal where the
 * flap closes. Side 2 is drawn in its own page's coordinates; a point of side 1
 * at (x, y) is behind (W - x, y) there.
 *
 * The finished views are made of the very same drawings: each flap is side 2
 * of the sheet, cut to the flap and folded onto the envelope by the flap's own
 * reflection (View.layers) - so what they show is what folding the print shows,
 * and the name and the seal are placed through the inverse of those folds to
 * come out upright.
 */
import { useId, type ReactNode } from 'react';
import { invert, toSvg, type Affine, type PaperStyle } from '@foldmycard/scene';
import { MonoFilter } from '../card-view.tsx';
import { hasPaper, PaperDefs, PaperFill } from '../paper.tsx';
import { t } from '../strings.ts';
import type { Envelope, Pt, View } from './designs.ts';

const pts = (p: Pt[]) => p.map((q) => `${q[0].toFixed(2)},${q[1].toFixed(2)}`).join(' ');
const centre = (p: Pt[]): Pt => [p.reduce((s, q) => s + q[0], 0) / p.length, p.reduce((s, q) => s + q[1], 0) / p.length];

export interface Decor {
  paper?: PaperStyle;
  name: string;
  /** The seal's letter; a heart when empty. */
  initial: string;
  /** Plain white outside, nothing printed on side 2. */
  plain: boolean;
  mono?: boolean;
  /** The colour of the writing and the seal. */
  ink: string;
}

const HAND = "'Caveat', 'Noto Naskh Arabic', 'Noto Sans Hebrew', cursive";
const SANS = "Inter, 'Segoe UI', Arial, sans-serif";

/** Side 1, the inside: the guides. */
export function EnvelopeInside({ env, className }: { env: Envelope; className?: string }) {
  const id = useId().replace(/:/g, '');
  const W = env.sheet.w, H = env.sheet.h;
  return (
    <svg className={className ?? 'env-svg'} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t('envSide1')}>
      <defs>
        <pattern id={`${id}-hatch`} patternUnits="userSpaceOnUse" width={3} height={3} patternTransform="rotate(45)">
          <rect width={3} height={3} fill="#e3e7eb" />
          <line x1={0} y1={0} x2={0} y2={3} stroke="#b9c1ca" strokeWidth={0.6} />
        </pattern>
      </defs>
      <rect width={W} height={H} fill="#ffffff" />
      {/* where the card goes */}
      <polygon points={pts(env.cardAt)} fill="none" stroke="#9aa6b2" strokeWidth={0.35} strokeDasharray="1 1.4" />
      <CardHint env={env} />
      {env.glue.map((g, i) => (
        <g key={i}>
          <polygon points={pts(g)} fill={`url(#${id}-hatch)`} stroke="#9aa6b2" strokeWidth={0.25} />
          <GlueLabel poly={g} />
        </g>
      ))}
      {env.folds.map((f, i) => (
        <polyline key={i} points={pts(f.line)} fill="none" stroke="#3f7fd9" strokeWidth={0.45} strokeDasharray="3 2" />
      ))}
      {env.folds.map((f, i) => <StepMark key={`n${i}`} at={midway(f.line)} n={f.step} line={f.line} />)}
      {env.cut.map((o, i) => (
        <polygon key={i} points={pts(o)} fill="none" stroke="#1b2430" strokeWidth={0.5} />
      ))}
      {env.cut.length > 0 && <Scissors at={topLeft(env.cut[0])} />}
      {env.seal && (
        <text x={env.seal.c[0]} y={env.seal.c[1]} textAnchor="middle" dominantBaseline="central" fontSize={2.8} fontFamily={SANS} fill="#7b8794">
          {t('envSealBack')}
        </text>
      )}
      <Legend env={env} />
    </svg>
  );
}

/** Halfway along a line. */
function midway(line: Pt[]): Pt {
  const len = line.slice(1).reduce((s, p, i) => s + Math.hypot(p[0] - line[i][0], p[1] - line[i][1]), 0);
  let go = len / 2;
  for (let i = 1; i < line.length; i++) {
    const a = line[i - 1], b = line[i], d = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (go <= d) return [a[0] + ((b[0] - a[0]) * go) / (d || 1), a[1] + ((b[1] - a[1]) * go) / (d || 1)];
    go -= d;
  }
  return line[line.length - 1];
}

const topLeft = (o: Pt[]): Pt => o.reduce((a, b) => (b[1] < a[1] - 0.01 || (Math.abs(b[1] - a[1]) < 0.01 && b[0] < a[0]) ? b : a));

function Scissors({ at }: { at: Pt }) {
  return (
    <g transform={`translate(${at[0] + 4} ${at[1] - 3.2})`} aria-hidden="true">
      <text fontSize={5} fill="#1b2430" fontFamily={SANS}>✂</text>
    </g>
  );
}

/** A fold's step number, in a small circle beside its line. */
function StepMark({ at, n, line }: { at: Pt; n: number; line: Pt[] }) {
  const a = line[0], b = line[line.length - 1];
  const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy) || 1;
  const off: Pt = [(-dy / L) * 3.2, (dx / L) * 3.2];
  return (
    <g>
      <circle cx={at[0] + off[0]} cy={at[1] + off[1]} r={2.3} fill="#3f7fd9" />
      <text x={at[0] + off[0]} y={at[1] + off[1]} textAnchor="middle" dominantBaseline="central" fontSize={2.8} fontWeight={700}
        fontFamily={SANS} fill="#ffffff">{n}</text>
    </g>
  );
}

function GlueLabel({ poly }: { poly: Pt[] }) {
  const [cx, cy] = centre(poly);
  const xs = poly.map((p) => p[0]), ys = poly.map((p) => p[1]);
  const tall = Math.max(...ys) - Math.min(...ys) > 1.15 * (Math.max(...xs) - Math.min(...xs));
  return (
    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={2.6} fontWeight={700} fontFamily={SANS} fill="#5b6b7c"
      transform={tall ? `rotate(-90 ${cx} ${cy})` : undefined}>{t('envGlueWord')}</text>
  );
}

/** "Your card goes here", in the middle of where it lies. */
function CardHint({ env }: { env: Envelope }) {
  const [cx, cy] = centre(env.cardAt);
  const xs = env.cardAt.map((p) => p[0]), ys = env.cardAt.map((p) => p[1]);
  const tall = Math.max(...ys) - Math.min(...ys) > 1.15 * (Math.max(...xs) - Math.min(...xs));
  return (
    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={3.4} fontFamily={SANS} fill="#9aa6b2"
      transform={tall ? `rotate(-90 ${cx} ${cy})` : undefined}>{t('envCardHere')}</text>
  );
}

/** The key to the lines, inside where the card lies (hidden once it is made). */
function Legend({ env }: { env: Envelope }) {
  const [cx, cy] = centre(env.cardAt);
  const xs = env.cardAt.map((p) => p[0]), ys = env.cardAt.map((p) => p[1]);
  const tall = Math.max(...ys) - Math.min(...ys) > 1.15 * (Math.max(...xs) - Math.min(...xs));
  const rows: [ReactNode, string][] = [
    ...(env.cut.length ? [[<line x1={0} y1={0} x2={8} y2={0} stroke="#1b2430" strokeWidth={0.5} />, t('envLegendCut')] as [ReactNode, string]] : []),
    [<line x1={0} y1={0} x2={8} y2={0} stroke="#3f7fd9" strokeWidth={0.45} strokeDasharray="3 2" />, t('envLegendFold')],
    [<rect x={0} y={-1.5} width={8} height={3} fill="#e3e7eb" stroke="#9aa6b2" strokeWidth={0.25} />, t('envLegendGlue')],
  ];
  return (
    <g transform={`${tall ? `rotate(-90 ${cx} ${cy}) ` : ''}translate(${cx - 24} ${cy + 7})`} aria-hidden="true">
      {rows.map(([mark, words], i) => (
        <g key={i} transform={`translate(0 ${i * 4.2})`}>
          {mark}
          <text x={10} y={0} dominantBaseline="central" fontSize={2.7} fontFamily={SANS} fill="#5b6b7c">{words}</text>
        </g>
      ))}
    </g>
  );
}

/**
 * Side 2, the outside, in its own page's coordinates: the paper all over, the
 * name in a label on the front, a seal where the flap closes, and the seal
 * sticker's face. `id` keeps its paper pattern's id apart from others on the page.
 */
function Outside({ env, decor, id }: { env: Envelope; decor: Decor; id: string }) {
  const W = env.sheet.w, H = env.sheet.h;
  if (decor.plain) return <rect width={W} height={H} fill="#ffffff" />;
  const toPage = (v: View, layer = 0): Affine => invert(v.layers.filter((l) => l.side === 2)[layer].toView);
  const front = toPage(env.address);
  const flaps = env.back.layers.filter((l) => l.side === 2);
  const closer = flaps.length ? invert(flaps[flaps.length - 1].toView) : null;
  const { c, w, h } = env.nameAt;
  return (
    <g filter={decor.mono ? `url(#${id}-mono)` : undefined}>
      <defs>
        <PaperDefs paper={decor.paper} id={`${id}-paper`} />
        {decor.mono && <MonoFilter id={`${id}-mono`} />}
      </defs>
      <rect width={W} height={H} fill="#ffffff" />
      {hasPaper(decor.paper) && <PaperFill paper={decor.paper} id={`${id}-paper`} W={W} H={H} />}
      {/* the front: the name on a label, or lines to write it by hand */}
      <g transform={toSvg(front)}>
        <rect x={c[0] - w / 2} y={c[1] - h / 2} width={w} height={h} rx={4} fill="#ffffff" fillOpacity={0.9} stroke={decor.ink} strokeOpacity={0.35} strokeWidth={0.4} />
        {decor.name.trim() ? (
          <>
            <text x={c[0]} y={c[1] - h * 0.18} textAnchor="middle" dominantBaseline="central" fontSize={Math.min(6, h * 0.16)} fontFamily={HAND} fill={decor.ink} fillOpacity={0.75}>
              {t('envFor')}
            </text>
            <text x={c[0]} y={c[1] + h * 0.12} textAnchor="middle" dominantBaseline="central" fontSize={Math.min(14, h * 0.34, (w * 1.6) / Math.max(4, [...decor.name].length))}
              fontFamily={HAND} fontWeight={700} fill={decor.ink}>{decor.name.trim()}</text>
          </>
        ) : (
          [0.25, 0.6].map((k) => (
            <line key={k} x1={c[0] - w * 0.38} x2={c[0] + w * 0.38} y1={c[1] - h / 2 + h * k + h * 0.12} y2={c[1] - h / 2 + h * k + h * 0.12}
              stroke={decor.ink} strokeOpacity={0.45} strokeWidth={0.35} />
          ))
        )}
      </g>
      {/* where the flap closes: the seal printed on it, when there is no sticker to cut out */}
      {closer && env.tipArt && <g transform={toSvg(closer)}><HeartSeal poly={env.tipArt} decor={decor} /></g>}
      {closer && env.sealAt && !env.seal && !env.tipArt && <g transform={toSvg(closer)}><Seal at={env.sealAt} r={8} decor={decor} /></g>}
      {/* the sticker's face, behind its cut circle on side 1, a little larger in case the sides do not quite line up */}
      {env.seal && <Seal at={[W - env.seal.c[0], env.seal.c[1]]} r={env.seal.r + 2} decor={decor} />}
    </g>
  );
}

/** The heart flap's tip, printed as a heart: its colour, a stitched edge, the letter. */
function HeartSeal({ poly, decor }: { poly: Pt[]; decor: Decor }) {
  const [cx, cy] = centre(poly);
  const xs = poly.map((p) => p[0]);
  const size = Math.max(...xs) - Math.min(...xs);
  return (
    <g>
      <polygon points={pts(poly)} fill={decor.ink} />
      <polygon points={pts(poly)} fill="none" stroke="#ffffff" strokeOpacity={0.7} strokeWidth={0.45} strokeDasharray="1.6 1.1"
        transform={`translate(${cx} ${cy}) scale(0.86) translate(${-cx} ${-cy})`} />
      {decor.initial && (
        <text x={cx} y={cy - size * 0.04} textAnchor="middle" dominantBaseline="central" fontSize={size * 0.32}
          fontFamily={HAND} fontWeight={700} fill="#ffffff">{decor.initial}</text>
      )}
    </g>
  );
}

/** A round seal: the letter, or a heart. */
function Seal({ at, r, decor }: { at: Pt; r: number; decor: Decor }) {
  const [x, y] = at;
  return (
    <g>
      <circle cx={x} cy={y} r={r} fill={decor.ink} />
      <circle cx={x} cy={y} r={r * 0.82} fill="none" stroke="#ffffff" strokeOpacity={0.7} strokeWidth={Math.max(0.3, r * 0.04)} strokeDasharray={`${r * 0.12} ${r * 0.08}`} />
      {decor.initial ? (
        <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={r * 1.15} fontFamily={HAND} fontWeight={700} fill="#ffffff">
          {decor.initial}
        </text>
      ) : (
        <path transform={`translate(${x} ${y + r * 0.06}) scale(${r * 0.42})`} d="M0 0.9 C-1.5 -0.2 -0.95 -1.55 0 -0.8 C0.95 -1.55 1.5 -0.2 0 0.9 Z" fill="#ffffff" />
      )}
    </g>
  );
}

/** Side 2 as printed, a page of its own. */
export function EnvelopeOutside({ env, decor, className }: { env: Envelope; decor: Decor; className?: string }) {
  const id = useId().replace(/:/g, '');
  return (
    <svg className={className ?? 'env-svg'} viewBox={`0 0 ${env.sheet.w} ${env.sheet.h}`} role="img" aria-label={t('envSide2')}>
      <Outside env={env} decor={decor} id={id} />
    </svg>
  );
}

/** The finished envelope, one side of it: the sheet's layers, each folded into place. */
export function EnvelopeView({ env, view, decor, label }: { env: Envelope; view: 'front' | 'back'; decor: Decor; label: string }) {
  const id = useId().replace(/:/g, '');
  const v = view === 'front' ? env.address : env.back;
  const xs = v.outline.map((p) => p[0]), ys = v.outline.map((p) => p[1]);
  const pad = 6;
  const box = [Math.min(...xs) - pad, Math.min(...ys) - pad, Math.max(...xs) - Math.min(...xs) + 2 * pad, Math.max(...ys) - Math.min(...ys) + 2 * pad];
  return (
    <svg className="env-svg env-finished" viewBox={box.join(' ')} role="img" aria-label={label}>
      <defs>
        {v.layers.map((l, i) => <clipPath key={i} id={`${id}-c${i}`}><polygon points={pts(l.clip)} /></clipPath>)}
        <filter id={`${id}-shadow`} x="-10%" y="-10%" width="120%" height="130%">
          <feDropShadow dx={0} dy={1.2} stdDeviation={1.4} floodOpacity={0.28} />
        </filter>
      </defs>
      <g filter={`url(#${id}-shadow)`}>
        <polygon points={pts(v.outline)} fill="#ffffff" />
      </g>
      {v.layers.map((l, i) => (
        <g key={i} transform={toSvg(l.toView)}>
          <g clipPath={`url(#${id}-c${i})`}>
            {l.side === 2 ? <Outside env={env} decor={decor} id={`${id}-o${i}`} /> : <rect width={env.sheet.w} height={env.sheet.h} fill="#f3efe6" />}
          </g>
          {l.side === 2 && v.layers.length > 1 && <polygon points={pts(l.clip)} fill="none" stroke="#1b2430" strokeOpacity={0.28} strokeWidth={0.35} />}
        </g>
      ))}
      <polygon points={pts(v.outline)} fill="none" stroke="#1b2430" strokeOpacity={0.35} strokeWidth={0.35} />
    </svg>
  );
}

/** A small drawing of the flat sheet, for choosing between envelopes. */
export function EnvelopeThumb({ env }: { env: Envelope }) {
  return (
    <svg className="env-thumb" viewBox={`0 0 ${env.sheet.w} ${env.sheet.h}`} aria-hidden="true">
      <rect width={env.sheet.w} height={env.sheet.h} rx={4} className="et-sheet" />
      {env.cut.slice(0, 1).map((o, i) => <polygon key={i} points={pts(o)} className="et-cut" />)}
      {env.folds.map((f, i) => <polyline key={i} points={pts(f.line)} className="et-fold" />)}
      {env.glue.map((g, i) => <polygon key={i} points={pts(g)} className="et-glue" />)}
    </svg>
  );
}
